import { AudioCapture } from './AudioCapture';
import { canOpenMic } from '../mic';
import { transcribeBlob, type TranscribeFn } from './backendTransport';
import { transcribeAudio } from '../../utils/transcribe';
import type {
  STTConfig,
  STTError,
  STTEventMap,
  STTProvider,
  STTResult,
} from './types';

/**
 * {@link STTProvider} fallback that records audio with `MediaRecorder` and
 * POSTs it to the backend `/speech/utterance` endpoint (GCP STT). This keeps
 * the existing cloud path alive for browsers without the Web Speech API
 * (e.g. Firefox) or insecure origins, with no backend changes.
 */
export class BackendSTTProvider implements STTProvider {
  private capture: AudioCapture | null = null;
  private config: STTConfig | null = null;
  /**
   * Set to true by `dispose()`. Guards against result/error emissions after
   * component unmount when `stopListening` is still in flight (transcription
   * round-trip completes after the component has already cleaned up).
   */
  private disposed = false;
  private resultHandlers = new Set<STTEventMap['result']>();
  private errorHandlers = new Set<STTEventMap['error']>();

  constructor(private readonly transcribe: TranscribeFn = transcribeAudio) {}

  /** Records from the turn's stream (see {@link STTConfig.stream}). */
  readonly capturesAudio = true;

  isSupported(): boolean {
    return typeof MediaRecorder !== 'undefined' && canOpenMic();
  }

  /**
   * Record from the turn's stream. The recorder opens a fresh stream every
   * turn (raw and liveness-checked on iOS, `speech/mic.ts`), which replaces the
   * old stream cached across turns: that cache existed because a second
   * default-constraints stream recorded silence on iOS, the same dead input
   * path #254 works around.
   */
  async startListening(config: STTConfig): Promise<void> {
    this.config = config;
    const capture = new AudioCapture();
    this.capture = capture;
    try {
      await capture.start({ stream: config.stream });
    } catch (err) {
      if (this.capture === capture) this.capture = null;
      this.emitError({
        code: 'unknown',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async stopListening(): Promise<void> {
    const capture = this.capture;
    const config = this.config;
    if (!capture || !config) {
      return;
    }
    this.capture = null;
    const blob = await capture.stop();
    // Guard: dispose() may have been called while capture.stop() was awaited
    // (component unmount). Emitting after dispose clears the handler sets would
    // silently drop the transcript — bail out instead.
    if (this.disposed) return;
    const outcome = await transcribeBlob(blob, config, this.transcribe);
    // Guard again: dispose() may have fired during the transcription round-trip.
    if (this.disposed) return;
    if (outcome.ok) {
      this.emitResult(outcome.result);
    } else {
      this.emitError(outcome.error);
    }
  }

  addEventListener<E extends keyof STTEventMap>(
    event: E,
    handler: STTEventMap[E]
  ): void {
    if (event === 'result') {
      this.resultHandlers.add(handler as STTEventMap['result']);
    } else {
      this.errorHandlers.add(handler as STTEventMap['error']);
    }
  }

  removeEventListener<E extends keyof STTEventMap>(
    event: E,
    handler: STTEventMap[E]
  ): void {
    if (event === 'result') {
      this.resultHandlers.delete(handler as STTEventMap['result']);
    } else {
      this.errorHandlers.delete(handler as STTEventMap['error']);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.capture?.dispose();
    this.capture = null;
    this.config = null;
    this.resultHandlers.clear();
    this.errorHandlers.clear();
  }

  private emitResult(result: STTResult): void {
    this.resultHandlers.forEach((handler) => handler(result));
  }

  private emitError(error: STTError): void {
    this.errorHandlers.forEach((handler) => handler(error));
  }
}
